import { Folder, FolderWithCount, CreateFolderData, SyncResult, ClassificationResult, ServerFolder, GENERAL_FOLDER_NAMES, PublicFolder } from '../types/Folder';
import { authService } from '../auth/AuthService';
import { APP_CONSTANTS } from '../constants/AppConstants';
import { IBookmarkWithFolder } from '../types/BookmarkExtensions';

class FolderService {
  private readonly API_BASE_URL = APP_CONSTANTS.API_BASE_URL;
  private readonly STORAGE_KEY = 'bookmarker_folders';
  private readonly MAX_FOLDERS = 50;

  // Generate UUID for local folder ID
  private generateId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  // Get all folders from local storage
  private getLocalFolders(): Folder[] {
    try {
      const foldersStr = localStorage.getItem(this.STORAGE_KEY);
      return foldersStr ? JSON.parse(foldersStr) : [];
    } catch (error) {
      console.error('Error reading folders from storage:', error);
      return [];
    }
  }

  // Save folders to local storage
  private saveLocalFolders(folders: Folder[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(folders));
    } catch (error) {
      console.error('Error saving folders to storage:', error);
    }
  }

  // Transform server folder to local folder format
  private transformServerFolder(serverFolder: ServerFolder): Folder {
    return {
      id: serverFolder._id, // Use server ID as local ID for synced folders
      serverId: serverFolder._id,
      userId: serverFolder.userId,
      name: serverFolder.name,
      description: serverFolder.description,
      color: serverFolder.color,
      icon: serverFolder.icon,
      position: serverFolder.position,
      bookmarkCount: serverFolder.bookmarkCount,
      createdAt: new Date(serverFolder.createdAt).getTime(),
      updatedAt: new Date(serverFolder.updatedAt).getTime(),
    };
  }

  // Transform local folder to server format
  private transformToServerFormat(folder: Folder): Partial<ServerFolder> {
    return {
      name: folder.name,
      description: folder.description,
      color: folder.color,
      icon: folder.icon,
      position: folder.position,
    };
  }

  // Get auth headers
  private getAuthHeaders(): Record<string, string> {
    return authService.getAuthHeaders();
  }

  // Check if authenticated
  private isAuthenticated(): boolean {
    return authService.isAuthenticated();
  }

  // Create folder (with offline support)
  async create(folderData: CreateFolderData): Promise<Folder> {
    // Check folder limit
    const existingFolders = this.getLocalFolders();
    if (existingFolders.length >= this.MAX_FOLDERS) {
      throw new Error(`Maximum folder limit (${this.MAX_FOLDERS}) reached`);
    }

    const now = Date.now();
    const newFolder: Folder = {
      id: this.generateId(),
      name: folderData.name,
      description: folderData.description,
      color: folderData.color,
      icon: folderData.icon,
      position: folderData.position ?? existingFolders.length,
      createdAt: now,
      updatedAt: now,
      serverId: '',
    };

    // Try to create on server first (if authenticated)
    if (this.isAuthenticated()) {
      try {
        const response = await fetch(`${this.API_BASE_URL}/folders`, {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify(this.transformToServerFormat(newFolder)),
        });

        if (response.ok) {
          const serverFolder: ServerFolder = await response.json();
          newFolder.serverId = serverFolder._id;
          newFolder.id = serverFolder._id; // Use server ID as primary ID
          newFolder.userId = serverFolder.userId;
          newFolder.bookmarkCount = serverFolder.bookmarkCount || 0;
          newFolder.createdAt = new Date(serverFolder.createdAt).getTime();
          newFolder.updatedAt = new Date(serverFolder.updatedAt).getTime();
        } else if (response.status === 400) {
          // Handle duplicate name - try to find existing folder
          const error = await response.json();
          if (error.message?.includes('already exists')) {
            // Fetch all folders and find matching one
            const allFolders = await this.getAll(true);
            const existing = allFolders.find(f => f.name.toLowerCase() === folderData.name.toLowerCase());
            if (existing) {
              return existing;
            }
          }
          throw new Error(error.message || 'Failed to create folder');
        }
      } catch (error) {
        console.error('Error creating folder on server:', error);
        // Continue with local creation
      }
    }

    // Save locally
    const folders = this.getLocalFolders();
    folders.push(newFolder);
    this.saveLocalFolders(folders);

    return newFolder;
  }

  // Get all folders (syncs from server if authenticated)
  async getAll(syncFromServer: boolean = false): Promise<Folder[]> {
    let folders = this.getLocalFolders();

    // Sync from server if authenticated
    if (syncFromServer && this.isAuthenticated()) {
      try {
        const response = await fetch(`${this.API_BASE_URL}/folders`, {
          method: 'GET',
          headers: this.getAuthHeaders(),
        });

        if (response.ok) {
          const serverFolders: ServerFolder[] = await response.json();

          // Transform server folders
          const transformedServerFolders = serverFolders.map(sf => this.transformServerFolder(sf));

          // Merge with local folders
          const localFoldersMap = new Map(folders.map(f => [f.serverId || f.id, f]));
          const mergedFolders: Folder[] = [];

          // Add/update folders from server
          for (const serverFolder of transformedServerFolders) {
            const localFolder = localFoldersMap.get(serverFolder.serverId || serverFolder.id);
            if (localFolder && !localFolder.serverId) {
              // Link local folder to server folder
              localFolder.serverId = serverFolder.serverId;
              localFolder.id = serverFolder.serverId;
              mergedFolders.push({ ...localFolder, ...serverFolder });
            } else {
              mergedFolders.push(serverFolder);
            }
            localFoldersMap.delete(serverFolder.serverId || serverFolder.id);
          }

          // Add remaining local folders (not on server)
          mergedFolders.push(...localFoldersMap.values());

          // Sort by position
          mergedFolders.sort((a, b) => a.position - b.position);

          folders = mergedFolders;
          this.saveLocalFolders(folders);
        }
      } catch (error) {
        console.error('Error fetching folders from server:', error);
      }
    }

    // Sort by position
    folders.sort((a, b) => a.position - b.position);
    return folders;
  }

  // Get folders with bookmark counts
  async getAllWithCounts(syncFromServer: boolean = true): Promise<FolderWithCount[]> {
    const folders = await this.getAll(syncFromServer);

    // Get bookmark counts from local storage
    const bookmarksStr = localStorage.getItem('bookmarks');
    const bookmarks: IBookmarkWithFolder[] = bookmarksStr ? JSON.parse(bookmarksStr) : [];

    // Count bookmarks per folder
    const folderCounts = new Map<string, number>();
    bookmarks.forEach(bookmark => {
      if (bookmark.folderId) {
        folderCounts.set(bookmark.folderId, (folderCounts.get(bookmark.folderId) || 0) + 1);
      }
    });

    return folders.map(folder => ({
      ...folder,
      bookmarkCount: folderCounts.get(folder.id) || folder.bookmarkCount || 0,
    }));
  }

  // Get folder by ID
  async getById(id: string, isServerId: boolean = false): Promise<Folder | null> {
    const folders = this.getLocalFolders();
    return folders.find(f => isServerId ? f.serverId === id : f.id === id) || null;
  }

  // Update folder
  async update(id: string, updates: Partial<Folder>): Promise<Folder> {
    const folders = this.getLocalFolders();
    const folderIndex = folders.findIndex(f => f.id === id);

    if (folderIndex === -1) {
      throw new Error('Folder not found');
    }

    const folder = folders[folderIndex];
    const updatedFolder: Folder = {
      ...folder,
      ...updates,
      updatedAt: Date.now(),
    };

    // Try to update on server first (if has serverId and authenticated)
    if (updatedFolder.serverId && this.isAuthenticated()) {
      try {
        const response = await fetch(`${this.API_BASE_URL}/folders/${updatedFolder.serverId}`, {
          method: 'PUT',
          headers: this.getAuthHeaders(),
          body: JSON.stringify(this.transformToServerFormat(updatedFolder)),
        });

        if (response.ok) {
          const serverFolder: ServerFolder = await response.json();
          // Update with server data
          updatedFolder.name = serverFolder.name;
          updatedFolder.description = serverFolder.description;
          updatedFolder.color = serverFolder.color;
          updatedFolder.icon = serverFolder.icon;
          updatedFolder.position = serverFolder.position;
          updatedFolder.updatedAt = new Date(serverFolder.updatedAt).getTime();
        } else if (response.status === 404) {
          // Folder deleted on server, recreate it
          const recreated = await this.create({
            name: updatedFolder.name,
            description: updatedFolder.description,
            color: updatedFolder.color,
            icon: updatedFolder.icon,
            position: updatedFolder.position,
          });
          return recreated;
        }
      } catch (error) {
        console.error('Error updating folder on server:', error);
        // Continue with local update
      }
    }

    // Update locally
    folders[folderIndex] = updatedFolder;
    this.saveLocalFolders(folders);

    return updatedFolder;
  }

  // Delete folder
  async delete(id: string, moveBookmarksToFolderId?: string): Promise<void> {
    const folders = this.getLocalFolders();
    const folder = folders.find(f => f.id === id);

    if (!folder) {
      throw new Error('Folder not found');
    }

    // Check if it's the General folder
    if (GENERAL_FOLDER_NAMES === (folder.name)) {
      throw new Error('Cannot delete the General folder');
    }

    // Move bookmarks to target folder (or General)
    const targetFolderId = moveBookmarksToFolderId || await this.ensureGeneralFolder().then(f => f?.id);

    if (targetFolderId) {
      const bookmarksStr = localStorage.getItem('bookmarks');
      if (bookmarksStr) {
        const bookmarks: IBookmarkWithFolder[] = JSON.parse(bookmarksStr);
        const updatedBookmarks = bookmarks.map(bookmark => {
          if (bookmark.folderId === id) {
            return { ...bookmark, folderId: targetFolderId };
          }
          return bookmark;
        });
        localStorage.setItem('bookmarks', JSON.stringify(updatedBookmarks));
      }
    }

    // Delete on server (if has serverId and authenticated)
    if (folder.serverId && this.isAuthenticated()) {
      try {
        const url = new URL(`${this.API_BASE_URL}/folders/${folder.serverId}`);
        if (moveBookmarksToFolderId) {
          url.searchParams.set('moveToFolderId', moveBookmarksToFolderId);
        }

        await fetch(url.toString(), {
          method: 'DELETE',
          headers: this.getAuthHeaders(),
        });
      } catch (error) {
        console.error('Error deleting folder on server:', error);
        // Continue with local deletion
      }
    }

    // Delete locally
    const updatedFolders = folders.filter(f => f.id !== id);
    this.saveLocalFolders(updatedFolders);
  }

  // Reorder folders
  async reorder(folderIds: string[]): Promise<void> {
    const folders = this.getLocalFolders();

    // Update positions locally
    folderIds.forEach((id, index) => {
      const folder = folders.find(f => f.id === id);
      if (folder) {
        folder.position = index;
        folder.updatedAt = Date.now();
      }
    });

    this.saveLocalFolders(folders);

    // Update on server (if authenticated)
    if (this.isAuthenticated()) {
      try {
        const serverIds = folderIds
          .map(id => folders.find(f => f.id === id)?.serverId)
          .filter((id): id is string => !!id);

        if (serverIds.length > 0) {
          await fetch(`${this.API_BASE_URL}/folders/reorder`, {
            method: 'POST',
            headers: this.getAuthHeaders(),
            body: JSON.stringify({ folderIds: serverIds }),
          });
        }
      } catch (error) {
        console.error('Error reordering folders on server:', error);
      }
    }
  }

  // Move bookmark to folder
  async moveBookmarkToFolder(bookmarkId: string, folderId?: string): Promise<void> {
    // Update bookmark's folderId locally
    const bookmarksStr = localStorage.getItem('bookmarks');
    if (bookmarksStr) {
      const bookmarks: IBookmarkWithFolder[] = JSON.parse(bookmarksStr);
      const bookmark = bookmarks.find(b => b.id === bookmarkId);

      if (bookmark) {
        bookmark.folderId = folderId;
        localStorage.setItem('bookmarks', JSON.stringify(bookmarks));
      }
    }

    // Update on server (if authenticated)
    if (this.isAuthenticated()) {
      try {
        await fetch(`${this.API_BASE_URL}/folders/bookmarks/move`, {
          method: 'POST',
          headers: this.getAuthHeaders(),
          body: JSON.stringify({ bookmarkId, folderId }),
        });
      } catch (error) {
        console.error('Error moving bookmark on server:', error);
      }
    }
  }

  // Sync all local folders to server
  async syncAllFoldersToServer(): Promise<SyncResult> {
    const result: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
    };

    if (!this.isAuthenticated()) {
      return result;
    }

    const folders = this.getLocalFolders();

    for (const folder of folders) {
      try {
        if (!folder.serverId) {
          // Create on server
          const response = await fetch(`${this.API_BASE_URL}/folders`, {
            method: 'POST',
            headers: this.getAuthHeaders(),
            body: JSON.stringify(this.transformToServerFormat(folder)),
          });

          if (response.ok) {
            const serverFolder: ServerFolder = await response.json();
            folder.serverId = serverFolder._id;
            folder.id = serverFolder._id;
            folder.userId = serverFolder.userId;
            result.synced++;
          } else {
            result.failed++;
            const error = await response.json();
            result.errors.push(`Failed to create folder "${folder.name}": ${error.message || 'Unknown error'}`);
          }
        } else {
          // Update on server
          const response = await fetch(`${this.API_BASE_URL}/folders/${folder.serverId}`, {
            method: 'PUT',
            headers: this.getAuthHeaders(),
            body: JSON.stringify(this.transformToServerFormat(folder)),
          });

          if (response.ok) {
            result.synced++;
          } else if (response.status === 404) {
            // Folder deleted on server, recreate it
            folder.serverId = '';
            const recreated = await this.create({
              name: folder.name,
              description: folder.description,
              color: folder.color,
              icon: folder.icon,
              position: folder.position,
            });
            if (recreated.serverId) {
              result.synced++;
            } else {
              result.failed++;
            }
          } else {
            result.failed++;
            const error = await response.json();
            result.errors.push(`Failed to update folder "${folder.name}": ${error.message || 'Unknown error'}`);
          }
        }
      } catch (error) {
        result.failed++;
        result.errors.push(`Error syncing folder "${folder.name}": ${error instanceof Error ? error.message : 'Unknown error'}`);
      }
    }

    // Save updated folders
    this.saveLocalFolders(folders);
    result.success = result.failed === 0;

    return result;
  }

  // Classify bookmark to folder (LLM-based)
  async classifyBookmarkToFolder(
    url: string,
    title?: string,
    description?: string,
    availableFolders: Folder[] = []
  ): Promise<ClassificationResult> {
    // If no folders, return null (default to General)
    if (availableFolders.length === 0) {
      const generalFolder = await this.ensureGeneralFolder();
      return {
        folderId: generalFolder?.id || null,
        confidence: 50,
        reasoning: 'No folders available, assigned to General folder',
      };
    }

    // Call LLM classification API
    if (!this.isAuthenticated()) {
      const generalFolder = await this.ensureGeneralFolder();
      return {
        folderId: generalFolder?.id || null,
        confidence: 50,
        reasoning: 'Not authenticated, assigned to General folder',
      };
    }

    try {
      const response = await fetch(`${this.API_BASE_URL}/bookmarks/classify`, {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify({
          url,
          title,
          description,
          availableFolders: availableFolders.map(f => ({
            name: f.name,
            description: f.description,
          })),
        }),
      });

      if (response.ok) {
        const data = await response.json();
        const { folder: folderName, confidence, reasoning } = data;

        // Find matching folder
        const matchedFolder = availableFolders.find(
          f => f.name.toLowerCase() === folderName.toLowerCase()
        );

        // Return folderId if confidence > threshold (50%)
        if (matchedFolder && confidence > 50) {
          return {
            folderId: matchedFolder.id,
            confidence,
            reasoning,
          };
        }
      }
    } catch (error) {
      console.error('Error classifying bookmark:', error);
    }

    // Fallback to General folder
    const generalFolder = await this.ensureGeneralFolder();
    return {
      folderId: generalFolder?.id || null,
      confidence: 50,
      reasoning: 'Assigned to General folder as default',
    };
  }

  // Ensure General/Others folder exists
  async ensureGeneralFolder(): Promise<Folder | null> {
    const folders = this.getLocalFolders();
    let generalFolder = folders.find(f => GENERAL_FOLDER_NAMES === (f.name));

    if (!generalFolder) {
      // Create General folder
      generalFolder = await this.create({
        name: 'Others',
        description: 'Default folder for bookmarks',
        color: '#6366F1',
        icon: 'folder',
        position: 0,
      });
    }

    return generalFolder;
  }

  async getFolderByName(name: string) {
    const folders = this.getLocalFolders();
    return folders.find(f => name.toLowerCase().includes(f.name.toLowerCase()));
  }

  /** List public folders (no auth). GET /folders/public */
  async getPublicFolders(): Promise<PublicFolder[]> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/folders/public`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        if (response.status === 404) return [];
        throw new Error('Failed to load public folders');
      }
      const data = await response.json();
      return Array.isArray(data) ? data : data.folders ?? [];
    } catch (error) {
      console.error('Error fetching public folders:', error);
      return [];
    }
  }

  /** Get single public folder by slug (no auth). GET /folders/public/:slug */
  async getPublicFolderBySlug(slug: string): Promise<PublicFolder | null> {
    try {
      const response = await fetch(`${this.API_BASE_URL}/folders/public/${encodeURIComponent(slug)}`, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json' },
      });
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error('Failed to load folder');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching public folder:', error);
      return null;
    }
  }
}

export const folderService = new FolderService();

